import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, User } from 'lucide-react';
import type { Processo } from '../servicos/servicoGerenciadorProcessos';
import IconButton from '../../../core/componentes/comuns/IconButton';
import { Tooltip } from '../../../core/componentes/Tooltip';
import { MicroMotion } from './MicroMotion';
import { useModal } from '../../../core/contextos/modalContext';
import { obterLabelTipoVertical, obterCoresTipoVertical, obterRotaPorTipoVertical } from '../tipos/tiposVerticais';

/**
 * CardProcesso
 *
 * Componente presentacional que exibe informações resumidas de um processo
 * e fornece ações rápidas (Continuar, Ver Detalhes, Menu).
 */
export function CardProcesso({ processo, onExcluir, onRenomear }: { processo: Processo; onExcluir?: (id: string) => void; onRenomear?: (id: string, novoNome: string) => void }): React.ReactElement {
  const navigate = useNavigate();
  
  // Hook para controlar modais de alerta/confirmação
  const { mostrarAlerta, mostrarConfirmacao } = useModal();
  
  const progressoStyle: React.CSSProperties = {
    height: 8,
    background: '#eee',
    borderRadius: 4,
    overflow: 'hidden'
  };
  const progressoInner: React.CSSProperties = {
    width: `${processo.progresso}%`,
    height: '100%',
    background: processo.status === 'concluido' ? '#4caf50' : '#1976d2',
    transition: 'width 550ms cubic-bezier(.22,.9,.2,1)'
  };

  // Derivar informações de exibição: etapa atual (1-based) e total

  return (
    <MicroMotion motionClassName="transition-transform duration-180 transform hover:-translate-y-1 hover:scale-[1.006] hover:shadow-lg" reducedMotionClassName="">
      <div style={{ border: '1px solid #ddd', padding: 12, borderRadius: 6, background: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <strong>{processo.nome}</strong>
          <div style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {processo.tipo} • {processo.status}
            {/* TAREFA-224: Badge de tipo de vertical */}
            {processo.tipoVertical && (
              <span 
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${obterCoresTipoVertical(processo.tipoVertical)}`}
                title={`Tipo: ${obterLabelTipoVertical(processo.tipoVertical)}`}
              >
                {obterLabelTipoVertical(processo.tipoVertical)}
              </span>
            )}
            {/* MULTI-TENANT: Mostrar nome do dono se não for o próprio usuário */}
            {processo.ehMeu === false && processo.nomeUsuario && (
              <span 
                className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
                title={`Criado por ${processo.nomeUsuario}`}
              >
                <User className="size-3" />
                {processo.nomeUsuario}
              </span>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12 }}>
              {processo.progresso}%
              {processo.progresso < 100 && ` • Etapa ${(processo.etapaAtual || 0) + 1}/6`}
            </div>
        </div>
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={progressoStyle}>
          <div style={progressoInner}></div>
        </div>
      </div>

      {/* Indicador de estado resumido */}
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        { (processo.dados && (processo.dados as Record<string, unknown>).aguardando_usuario) ? (
          <span
            title="Aguardando ação do usuário"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#fff7ed',
              color: '#92400e',
              padding: '4px 8px',
              borderRadius: 999,
              fontSize: 12,
              border: '1px solid #f59e0b'
            }}
            aria-label="Aguardando ação do usuário"
          >
            <Clock style={{ width: 14, height: 14 }} />
            <strong>Aguardando</strong>
          </span>
        ) : processo.status === 'em_andamento' ? (
          <span style={{ color: '#0b74d1', fontSize: 12 }}>Processando</span>
        ) : processo.status === 'concluido' ? (
          <span style={{ color: '#166534', fontSize: 12 }}>Concluído</span>
        ) : (
          <span style={{ color: '#6b7280', fontSize: 12 }}>Pendente</span>
        ) }
      </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Tooltip text="Continuar">
          <IconButton srLabel="Continuar processo" onClick={() => {
          // TAREFA-224: Rotear para a página correta baseado no tipo de vertical
          const rota = obterRotaPorTipoVertical(processo.tipoVertical);
          navigate(`${rota}?processo_id=${encodeURIComponent(processo.id)}`);
        }}>
          {/* Right arrow / continue icon */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          </IconButton>
        </Tooltip>
        <Tooltip text="Excluir">
          <IconButton srLabel="Excluir processo" onClick={() => {
          mostrarConfirmacao({
            titulo: 'Confirmar Exclusão',
            mensagem: 'Excluir permanentemente este processo? Esta ação é irreversível.',
            aoConfirmar: () => {
              if (onExcluir) onExcluir(processo.id);
            },
          });
        }}>
          {/* Trash icon */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M3 6h18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 6v14a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          </IconButton>
        </Tooltip>
        <Tooltip text="Renomear">
          <IconButton srLabel="Renomear processo" onClick={() => {
          // TODO: Implementar modal de input customizado (TAREFA futura)
          // Por enquanto, usando prompt nativo pois requer input do usuário
          const novoNome = window.prompt('Novo nome do processo:', processo.nome || '');
          if (novoNome === null) return; // usuário cancelou
          const nomeTrim = (novoNome || '').trim();
          if (!nomeTrim) {
            mostrarAlerta({
              titulo: 'Nome Inválido',
              mensagem: 'O nome do processo não pode ficar vazio.',
            });
            return;
          }
          if (onRenomear) onRenomear(processo.id, nomeTrim);
        }}>
          {/* Pencil icon */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M3 21v-3.75L14.81 5.44a2 2 0 0 1 2.82 0l1.94 1.94a2 2 0 0 1 0 2.82L7.75 21H3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          </IconButton>
        </Tooltip>
      </div>
      </div>
    </MicroMotion>
  );
}
